import { BankAccountExportSelection } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/BankAccounts/bankAccountsExport.types.ts';
import {
	BulkOperationType,
	getOperationType,
} from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/common/bulk-action-utils';
import {
	getTableExcludedSelection,
	getTableSelectedIds,
} from '@dealstack/frontend/shared/components/Table/MantineTable/table-utils.ts';
import {
	getTableMeta,
	TableData,
} from '@dealstack/frontend/shared/components/Table/MantineTable/types';
import { Table } from '@tanstack/react-table';
import { defaultTheme } from '@dealstack/frontend/shared/mechanism/styleguide/defaultTheme.ts';
import { getBankAccountDetailLines } from '@dealstack/frontend/shared/utils/bankAccountDisplay.utils.ts';
import {
	BankAccountValidationOutcomeFilter,
	BankAccountVerificationBucketFilter,
	DataMipInvestorBankAccountRes,
	VerificationResult,
} from '@dealstack/shared/lib/generated';

// Must mirror the server's mapping in investorBankAccount.utils.ts.
export const getValidationOutcome = (
	account: Pick<DataMipInvestorBankAccountRes, 'validation'>
): BankAccountValidationOutcomeFilter => {
	if (!account.validation) {
		return BankAccountValidationOutcomeFilter.NOT_CHECKED;
	}

	switch (account.validation.result) {
		case VerificationResult.VERIFIED:
		case VerificationResult.MATCH:
			return BankAccountValidationOutcomeFilter.COMPLIANT;
		case VerificationResult.NOT_VERIFIED:
		case VerificationResult.NO_MATCH:
		case VerificationResult.ERROR:
			return BankAccountValidationOutcomeFilter.FAILED;
		case VerificationResult.CLOSE_MATCH:
		case VerificationResult.INCONCLUSIVE:
			return BankAccountValidationOutcomeFilter.INCONCLUSIVE;
		default:
			return BankAccountValidationOutcomeFilter.NOT_CHECKED;
	}
};

export const VALIDATION_OUTCOME_ORDER = [
	BankAccountValidationOutcomeFilter.COMPLIANT,
	BankAccountValidationOutcomeFilter.FAILED,
	BankAccountValidationOutcomeFilter.INCONCLUSIVE,
	BankAccountValidationOutcomeFilter.NOT_CHECKED,
] as const;

export const VALIDATION_OUTCOME_LABEL_KEYS: Record<BankAccountValidationOutcomeFilter, string> = {
	[BankAccountValidationOutcomeFilter.COMPLIANT]: 'bank_accounts.validation.compliant',
	[BankAccountValidationOutcomeFilter.FAILED]: 'bank_accounts.validation.failed',
	[BankAccountValidationOutcomeFilter.INCONCLUSIVE]: 'bank_accounts.validation.inconclusive',
	[BankAccountValidationOutcomeFilter.NOT_CHECKED]: 'bank_accounts.validation.not_checked',
};

// Mantine palette keys for the badge; the dots below need raw theme values.
export const VALIDATION_OUTCOME_BADGE_COLORS: Record<BankAccountValidationOutcomeFilter, string> = {
	[BankAccountValidationOutcomeFilter.COMPLIANT]: 'teal',
	[BankAccountValidationOutcomeFilter.FAILED]: 'red.8',
	[BankAccountValidationOutcomeFilter.INCONCLUSIVE]: 'orange',
	[BankAccountValidationOutcomeFilter.NOT_CHECKED]: 'dark.0',
};

export const getValidationOutcomeColor = (outcome: BankAccountValidationOutcomeFilter): string => {
	switch (outcome) {
		case BankAccountValidationOutcomeFilter.COMPLIANT:
			return defaultTheme.v3.colors.teal[8];
		case BankAccountValidationOutcomeFilter.FAILED:
			return defaultTheme.v3.colors.red[8];
		case BankAccountValidationOutcomeFilter.INCONCLUSIVE:
			return defaultTheme.v3.colors.orange[6];
		case BankAccountValidationOutcomeFilter.NOT_CHECKED:
			return defaultTheme.v3.colors.dark[0];
	}
};

export const getValidationOutcomeBorderColor = (
	outcome: BankAccountValidationOutcomeFilter
): string =>
	outcome === BankAccountValidationOutcomeFilter.FAILED
		? defaultTheme.v3.colors.red[2]
		: getValidationOutcomeColor(outcome);

export const VERIFICATION_BUCKET_LABEL_KEYS: Record<BankAccountVerificationBucketFilter, string> = {
	[BankAccountVerificationBucketFilter.COMPLETE]: 'bank_accounts.status.complete',
	[BankAccountVerificationBucketFilter.PENDING]: 'bank_accounts.status.pending',
	[BankAccountVerificationBucketFilter.DECLINED]: 'bank_accounts.status.declined',
};

export const getAccountDetails = (account: DataMipInvestorBankAccountRes) =>
	getBankAccountDetailLines({
		bankName: account.bankName,
		currency: account.currency,
		identifiers: account.identifiers,
		accountNumber: account.accountNumber,
		iban: account.iban,
	});

// Under manual pagination the shared table flips to exclusion semantics once "All" is
// picked: entries in rowSelection are the rows left OUT. Read that intent rather than
// getSelectedRowModel(), which can only ever resolve rows on the page in front of you.
export const getExportSelection = <T extends TableData>(
	table: Table<T>
): BankAccountExportSelection => {
	const { manualPagination, greedySelecting } = getTableMeta(table);

	if (getOperationType(table) === BulkOperationType.selected) {
		return {
			operationType: BulkOperationType.selected,
			selectedIds: getTableSelectedIds(table),
		};
	}

	return {
		operationType: BulkOperationType.filtered,
		excludedIds:
			manualPagination && greedySelecting
				? getTableSelectedIds(table)
				: getTableExcludedSelection(table),
	};
};

// The count on the action label must be the count the export produces, so it comes from
// the same intent: an explicit pick is its own size, "all" is the filtered total less
// whatever was ticked off.
export const getExportSelectionCount = (
	selection: BankAccountExportSelection,
	filteredTotal: number
): number =>
	selection.operationType === BulkOperationType.selected
		? selection.selectedIds.length
		: Math.max(0, filteredTotal - selection.excludedIds.length);

// Which kind of non-natural owner a row belongs to, if any. The programme's own account
// and a personal investment vehicle (PIV) both list a company name where every other row lists a
// person, so the badge names what the reader is looking at.
export const getOwnerBadge = (
	account: Pick<DataMipInvestorBankAccountRes, 'isProgrammeAccount' | 'isPivAccount'>,
	translate: (key: string) => string
): { label: string; color: string; textColor?: string } | null => {
	if (account.isProgrammeAccount) {
		return {
			label: translate('bank_accounts.table.owner.programme'),
			color: 'blue.6',
		};
	}
	if (account.isPivAccount) {
		return {
			label: translate('bank_accounts.table.owner.piv'),
			color: 'gray.4',
			textColor: 'dark.8',
		};
	}
	return null;
};
